import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  DEFAULT_PROMPT,
  DEFAULT_RUBRIC,
  DEFAULT_SAMPLE_COUNT,
  makeDefaultMetrics
} from '../shared/defaults'
import { hasValidMetric, makeMetricRow, NAME_RE } from '../shared/judgeSpec'

const EvalRunFormContext = createContext(null)

export const useEvalRunForm = () => {
  const ctx = useContext(EvalRunFormContext)
  if (!ctx) throw new Error('useEvalRunForm must be used within an EvalRunFormProvider')
  return ctx
}

// @sync-with: calc-api/apps/agents/src/agents/tools/eval/proposeMetric.js
function validateMetricUpsert (args) {
  if (!args || typeof args !== 'object') return 'Missing args'
  if (typeof args.name !== 'string' || !NAME_RE.test(args.name)) return 'Invalid metric name'
  if (args.type !== 'passfail' && args.type !== 'rating') return 'Invalid metric type'
  if (typeof args.description !== 'string' || !args.description.length) return 'Missing description'
  const w = Number(args.weight)
  if (!Number.isFinite(w) || w < 0) return 'Invalid weight'
  return null
}

// @sync-with: calc-api/apps/agents/src/agents/tools/eval/removeMetric.js
function validateMetricRemove (args) {
  if (!args || typeof args !== 'object') return 'Missing args'
  if (typeof args.name !== 'string' || !args.name.length) return 'Invalid metric name'
  return null
}

// @sync-with: calc-api/apps/agents/src/agents/tools/eval/proposeRubric.js
function validateRubric (args) {
  if (!args || typeof args !== 'object') return 'Missing args'
  if (typeof args.rubric !== 'string' || !args.rubric.length) return 'Empty rubric'
  return null
}

// @sync-with: calc-api/apps/agents/src/agents/tools/eval/proposePrompt.js
function validatePrompt (args) {
  if (!args || typeof args !== 'object') return 'Missing args'
  if (typeof args.prompt !== 'string' || !args.prompt.length) return 'Empty prompt'
  return null
}

function snapshotForm (form) {
  return JSON.stringify({
    name: form.name,
    prompt: form.prompt,
    rubric: form.rubric,
    metrics: form.metrics,
    sampleCount: form.sampleCount,
    skillId: form.skillId,
    skillTag: form.skillTag,
    modelOverride: form.modelOverride,
    judgeModelId: form.judgeModelId,
    temperature: form.temperature,
    focus: form.focus
  })
}

function EvalRunFormProvider ({ children }) {
  const [name, setName] = useState('')
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT)
  const [rubric, setRubric] = useState(DEFAULT_RUBRIC)
  const [metrics, setMetrics] = useState(makeDefaultMetrics)
  const [sampleCount, setSampleCount] = useState(DEFAULT_SAMPLE_COUNT)
  const [skillId, setSkillId] = useState('')
  const [skillTag, setSkillTag] = useState('')
  const [modelOverride, setModelOverride] = useState('')
  const [judgeModelId, setJudgeModelId] = useState('')
  const [temperature, setTemperature] = useState(null)
  const [focus, setFocus] = useState(null)
  const [appliedProposalIds, setAppliedProposalIds] = useState(() => new Set())

  const baselineRef = useRef(null)

  const upsertMetric = useCallback((patch) => {
    if (!patch?.name) return
    setMetrics(prev => {
      const i = prev.findIndex(m => m.name === patch.name)
      if (i >= 0) {
        const next = prev.slice()
        next[i] = { ...prev[i], ...patch }
        return next
      }
      return [...prev, makeMetricRow({ ...patch })]
    })
  }, [])

  const removeMetric = useCallback((target) => {
    setMetrics(prev => prev.filter(m => (target.id ? m.id !== target.id : m.name !== target.name)))
  }, [])

  const updateMetric = useCallback((id, patch) => {
    setMetrics(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m))
  }, [])

  const addMetric = useCallback(() => {
    setMetrics(prev => [...prev, makeMetricRow()])
  }, [])

  const replaceMetrics = useCallback((next) => {
    setMetrics(Array.isArray(next) ? next : [])
  }, [])

  const markProposalApplied = useCallback((proposalId) => {
    if (!proposalId) return
    setAppliedProposalIds(prev => {
      if (prev.has(proposalId)) return prev
      const next = new Set(prev)
      next.add(proposalId)
      return next
    })
  }, [])

  const applyProposal = useCallback(({ tool, toolCallId, args }) => {
    let reason = null
    switch (tool) {
      case 'propose_metric':
        reason = validateMetricUpsert(args)
        if (!reason) {
          upsertMetric({
            name: args.name,
            type: args.type,
            description: args.description,
            weight: Number(args.weight)
          })
        }
        break
      case 'remove_metric':
        reason = validateMetricRemove(args)
        if (!reason) removeMetric({ name: args.name })
        break
      case 'propose_rubric':
        reason = validateRubric(args)
        if (!reason) setRubric(args.rubric)
        break
      case 'propose_prompt':
        reason = validatePrompt(args)
        if (!reason) setPrompt(args.prompt)
        break
      default:
        reason = `Unknown proposal tool: ${tool}`
    }

    if (reason) return { ok: false, reason }
    markProposalApplied(toolCallId)
    return { ok: true }
  }, [upsertMetric, removeMetric, setRubric, setPrompt, markProposalApplied])

  const resetForm = useCallback(() => {
    setName('')
    setPrompt(DEFAULT_PROMPT)
    setRubric(DEFAULT_RUBRIC)
    setMetrics(makeDefaultMetrics())
    setSampleCount(DEFAULT_SAMPLE_COUNT)
    setSkillId('')
    setSkillTag('')
    setModelOverride('')
    setJudgeModelId('')
    setTemperature(null)
    setFocus(null)
    setAppliedProposalIds(new Set())
    baselineRef.current = null
  }, [])

  const currentSnapshot = useMemo(() => snapshotForm({
    name,
    prompt,
    rubric,
    metrics,
    sampleCount,
    skillId,
    skillTag,
    modelOverride,
    judgeModelId,
    temperature,
    focus
  }), [name, prompt, rubric, metrics, sampleCount, skillId, skillTag, modelOverride, judgeModelId, temperature, focus])

  if (baselineRef.current === null) {
    baselineRef.current = currentSnapshot
  }
  const isDirty = currentSnapshot !== baselineRef.current
  const isDirtyRef = useRef(isDirty)
  isDirtyRef.current = isDirty

  const validMetricCount = useMemo(() => metrics.filter(m => m.name).length, [metrics])
  const hasValid = useMemo(() => hasValidMetric(metrics), [metrics])

  const value = useMemo(() => ({
    name,
    setName,
    prompt,
    setPrompt,
    rubric,
    setRubric,
    metrics,
    setMetrics,
    sampleCount,
    setSampleCount,
    skillId,
    setSkillId,
    skillTag,
    setSkillTag,
    modelOverride,
    setModelOverride,
    judgeModelId,
    setJudgeModelId,
    temperature,
    setTemperature,
    focus,
    setFocus,
    upsertMetric,
    removeMetric,
    updateMetric,
    addMetric,
    replaceMetrics,
    isDirty,
    isDirtyRef,
    resetForm,
    appliedProposalIds,
    markProposalApplied,
    applyProposal,
    validMetricCount,
    hasValidMetric: hasValid
  }), [
    name, prompt, rubric, metrics, sampleCount,
    skillId, skillTag, modelOverride, judgeModelId, temperature, focus,
    upsertMetric, removeMetric, updateMetric, addMetric, replaceMetrics,
    isDirty, resetForm,
    appliedProposalIds, markProposalApplied, applyProposal,
    validMetricCount, hasValid
  ])

  return (
    <EvalRunFormContext.Provider value={value}>
      {children}
    </EvalRunFormContext.Provider>
  )
}

EvalRunFormProvider.propTypes = {
  children: PropTypes.node
}

export default EvalRunFormProvider
